import { api } from "@/api/client";

// Модуль ФБС (Wildberries FBS). Процесс и экраны — docs/fbs-process.md.

// --- кабинеты и главный экран (S0) -------------------------------------------

export interface Cabinet {
  id: number;
  name: string;
  is_active: boolean;
  position: number;
  wb_warehouse_id: number;
  organization_ref: string;
  organization_name: string;
  kontragent_ref: string | null;
  kontragent_name: string | null;
  contract_ref: string | null;
  contract_name: string | null;
  /** Реквизиты продажи: покупатель-маркетплейс, комиссионный договор, счёт. */
  sale_kontragent_ref: string | null;
  sale_kontragent_name: string | null;
  sale_contract_ref: string | null;
  sale_contract_name: string | null;
  bank_account_ref: string | null;
  bank_account_name: string | null;
  /** Папка юрлица в Infisical, откуда берётся токен WB. Не секрет. */
  infisical_slug: string;
  /** Кабинет ходит в ТЕСТОВЫЙ контур WB: другой базовый адрес и другая папка
   *  токена. Боевой токен на песочном адресе даёт 403 «production token used
   *  for sandbox endpoint». */
  wb_sandbox: boolean;
  ready_for_documents: boolean;
  ready_error: string | null;
  /** Готовность к документам продажи — отдельно от передачи. */
  ready_for_sale: boolean;
  sale_error: string | null;
  /** Настроен ли токен WB (сам токен наружу не отдаётся никогда). */
  token_ready: boolean;
  token_error: string | null;
  /** Склады продавца кабинета: работают одновременно, у каждого своя поставка. */
  warehouses: CabinetWarehouse[];
}

export interface CabinetWarehouseSupply {
  wb_warehouse_id: number;
  name: string;
  /** Тип открытой поставки склада: пока она идёт, второй тип начать нельзя. */
  supply_is_b2b?: boolean;
  supply_id: string | null;
  supply_uuid: string | null;
  supply_status: string | null;
  open_boxes: number;
}

export interface CabinetWarehouse {
  wb_warehouse_id: number;
  name: string;
  /** Склад 1С (комната ФБС), которому соответствует этот склад продавца. */
  one_c_warehouse_ref?: string | null;
  one_c_warehouse_name?: string | null;
}

export interface CabinetPayload {
  name: string;
  infisical_slug?: string;
  wb_sandbox?: boolean;
  /** Токен WB открытым текстом — только на запись, и только там, где
   *  инсталляция хранит токены у себя. Пусто = «не менять». */
  wb_token?: string;
  /** Полный список складов. Не передан — не трогаем; передан — заменяет целиком. */
  warehouses?: CabinetWarehouse[];
  wb_warehouse_id: number;
  organization_ref: string;
  organization_name?: string;
  kontragent_ref?: string | null;
  kontragent_name?: string | null;
  contract_ref?: string | null;
  contract_name?: string | null;
  sale_kontragent_ref?: string | null;
  sale_kontragent_name?: string | null;
  sale_contract_ref?: string | null;
  sale_contract_name?: string | null;
  bank_account_ref?: string | null;
  bank_account_name?: string | null;
  position?: number;
  is_active?: boolean;
}

export interface CabinetSummary {
  cabinet_id: number;
  name: string;
  organization_ref: string;
  organization_name: string;
  wb_warehouse_id: number;
  /** Кабинет в тестовом контуре WB — на главном экране это бейдж. */
  wb_sandbox: boolean;
  pending: number;
  picked: number;
  cancelled: number;
  deferred: number;
  /** Доступно к сбору по юрлицу = pending + deferred. Считает сервер. */
  collectable: number;
  /** Доступно к сбору по типу продажи: B2B уезжает ОТДЕЛЬНОЙ поставкой —
   *  WB не даёт смешивать их в одной. */
  collectable_b2b: number;
  collectable_b2c: number;
  /** Тип ОТКРЫТОЙ поставки: пока она идёт, второй тип начать нельзя. */
  supply_is_b2b?: boolean;
  articles_pending: number;
  nearest_deadline: string | null;
  zero_orders: number;
  /** Номер поставки в WB (WB-GI-…) — то, что видит человек. */
  /** Склады продавца и открытая поставка КАЖДОГО: они работают одновременно. */
  warehouses: CabinetWarehouseSupply[];
  supply_id: string | null;
  /** Наш id открытой поставки — по нему строятся переходы на экраны. */
  supply_uuid: string | null;
  supply_status: string | null;
  open_boxes: number;
  accepted_today: number;
  /** Сколько штук ещё можно отгрузить сегодня по 15 ₽ (дальше 75 ₽). */
  cheap_left: number;
  error: string | null;
}

export interface FbsOverview {
  /** Доступно к сбору по всем юрлицам = новые + отложенные (считает сервер). */
  collectable: number;
  picked: number;
  cabinets: CabinetSummary[];
}

export interface CabinetSync {
  cabinet_id: number;
  cabinet_name: string;
  fetched: number;
  created: number;
  updated: number;
  skipped_other_warehouse: number;
  /** Номера заказов, отменённых покупателем (сняты из списка к сбору). */
  cancelled: number[];
  /** Номера заказов, собранных в кабинете WB мимо портала (статус `external`). */
  external: number[];
  error: string | null;
}

export interface SyncResult {
  cabinets: CabinetSync[];
  overview: FbsOverview;
}

// --- сбор в комнате (S2) -----------------------------------------------------

export interface Box {
  box_id: string;
  position: number;
  /** null, пока короб открыт: грузоместо в WB создаётся при закрытии. */
  wb_trbx_id: string | null;
  status: string;
  orders: number;
  weight_kg: number;
  /** Сколько позиций в коробе без веса в 1С — вес показываем честно неполным. */
  without_weight: number;
  can_close: boolean;
  overweight: boolean;
}

export interface Supply {
  supply_id: string;
  wb_supply_id: string;
  status: string;
  cabinet_id: number;
  cabinet_name: string;
  /** Прогресс: собрано в этой поставке / доступно к сбору по юрлицу. */
  picked: number;
  collectable: number;
  boxes: Box[];
  active_box: Box | null;
}

/**
 * Один скан. Два вида товара — маркированный и нет.
 *
 * `cim` — код Честного знака: партия и серийник приходят из самого кода.
 * `ean13` — товар без маркировки (напечатан до перехода на ЧЗ): партию
 * оператор вводит с этикетки, потому что взять её неоткуда.
 */
export interface PickRequest {
  /** Тип поставки, выбранный на главном экране. Нужен только на ПЕРВОМ
   *  скане: у открытой поставки тип уже задан и не меняется. */
  b2b?: boolean;
  /** По умолчанию бэкенд считает скан кодом ЧЗ. */
  scan_kind?: "cim" | "ean13";
  /** Полный код ЧЗ — он же защита от повторного скана в пределах поставки. */
  cim_code?: string;
  gtin?: string;
  serial?: string;
  /** Ветка ean13: сам штрихкод + партия с этикетки. */
  ean13?: string;
  partia_name?: string;
  /** «В 1С такой партии нет» — оператор подтвердил в модалке. */
  accept_without_partia?: boolean;
  /** На какой склад собираем. Не задан — первый склад кабинета. */
  wb_warehouse_id?: number | null;
}

export interface PickResult {
  pick_id: string;
  wb_order_id: number;
  article: string;
  nomenclature_name: string;
  characteristic_name: string | null;
  partia_name: string | null;
  expiration: string | null;
  /** null — короб ещё не создан: WB не даёт грузоместо в пустой поставке. */
  box_position: number | null;
  sticker_printed: boolean;
  issue: string | null;
  warning: string | null;
}

/** Код ошибки скана — определяет состояние героя на экране S2.
 *  `partia_not_found` героем НЕ показывается: это вопрос оператору
 *  («партии нет в 1С — принять без неё?»), а не ошибка скана. */
export type PickErrorCode =
  | "duplicate"
  | "unknown_product"
  | "barcode_ambiguous"
  | "wrong_cabinet"
  | "no_task"
  | "partia_not_found"
  | "order_cancelled"
  | "mp_binding_unavailable"
  | "error";

export interface PickErrorDetail {
  code: PickErrorCode;
  detail: string;
  cabinet_id?: number;
  cabinet_name?: string;
}

export interface PickListRow {
  article: string;
  /** Характеристика 1С (размер/цвет), опознанная по штрихкоду задания. У
   *  одного артикула их бывает несколько — строки тогда РАЗНЫЕ. */
  characteristic_name: string;
  nomenclature_name: string;
  /** Сколько ещё осталось собрать (доступные задания). */
  needed: number;
  /** Сколько уже собрано (задания в статусе picked). */
  picked: number;
  /** Остаток комнаты. В `pick-state` всегда null: там список строится без 1С,
   *  подсказку экран тянет отдельно (`getRoomStock`) и редко. */
  in_room: number | null;
}

/** Причина, по которой позиция попала в «Разбор» (SH-FIX). */
// ⚠️ `no_sgtin` ставит профиль, передающий КИЗ в WB (вторая инсталляция).
// Без него в объединении подпись причины приходилось брать через
// `Record<string, string>`, то есть без проверки типов.
// `sgtin_rejected` — КИЗ отправлен, но проверку WB не прошёл: поставку с ним WB
// в доставку не пропустит. Текст статуса — `sgtin_decision_text` строки.
export type PickIssue =
  | "no_sticker"
  | "no_partia"
  | "no_expiration"
  | "no_sgtin"
  | "sgtin_rejected";

export interface IssueRow {
  pick_id: string;
  wb_order_id: number;
  cabinet_id: number;
  article: string;
  nomenclature_name: string;
  characteristic_name: string | null;
  partia_name: string | null;
  box_position: number | null;
  issue: PickIssue;
  /** Статус проверки КИЗ у WB: код, текст по-русски, когда читали. */
  sgtin_decision: string | null;
  sgtin_decision_text: string | null;
  sgtin_checked_at: string | null;
  /** КИЗ убран из задания кнопкой «Отправить без кода» — повторно не отправляется. */
  sgtin_removed_at: string | null;
  /** Наклейка на штуке — по ней её ищут в коробе («Заменить товар»). */
  sticker_part_a: string | null;
  sticker_part_b: string | null;
  created_at: string;
}

export interface Issues {
  rows: IssueRow[];
  total: number;
  no_sticker: number;
  no_partia: number;
  no_expiration: number;
}

/**
 * Всё состояние экрана сбора одним ответом: юрлицо, поставка, список к сборке,
 * «Разбор». Один запрос вместо трёх — он перечитывается после КАЖДОГО скана.
 */
export interface Warehouse {
  wb_warehouse_id: number;
  name: string;
}

export interface PickState {
  cabinet_id: number;
  cabinet_name: string;
  /** Склады кабинета: работают ОДНОВРЕМЕННО, у каждого своя поставка. */
  warehouses: Warehouse[];
  wb_warehouse_id: number | null;
  /** null — поставки ещё нет: она заводится ПЕРВЫМ сканом, а не входом на экран. */
  supply: Supply | null;
  rows: PickListRow[];
  /** Сколько всего осталось собрать по юрлицу (сумма `needed` строк). */
  needed_total: number;
  picked_total: number;
  issues: Issues;
  /** Точка сборки склада — по ней экран спрашивает статус ЕЁ принтера.
   *  Ключа НЕТ вовсе, пока склад не отдан точке (инсталляция без точек). */
  point?: PickStatePoint;
}

/** Точка сборки, которой отдан склад пика. */
export interface PickStatePoint {
  id: number;
  name: string;
}

/** Остаток комнаты ФБС по артикулам — подсказка «где искать». Ошибка приходит
 *  отдельным полем: «не смогли спросить» и «пусто» — разные вещи. */
export interface RoomStock {
  stock: Record<string, number>;
  error: string | null;
}

/** Артикул → наименование 1С для списка сбора. Ошибка отдельным
 *  полем — по той же причине, что и у остатка комнаты. */
export interface ArticleNames {
  names: Record<string, string>;
  error: string | null;
}

export interface BoxItem {
  pick_id: string;
  wb_order_id: number;
  article: string;
  nomenclature_name: string;
  partia_name: string | null;
  issue: string | null;
  sgtin_decision: string | null;
  sgtin_decision_text: string | null;
  sgtin_checked_at: string | null;
  created_at: string;
}


// --- завершение поставки (S4) ------------------------------------------------

export interface UnpickedTask {
  task_id: string;
  wb_order_id: number;
  cabinet_id: number;
  cabinet_name: string;
  article: string;
  /** Заказ на нулевой остаток — отмена без штрафа. */
  is_zero_order: boolean;
  deadline_at: string | null;
  status: string;
  /** Что напечатано на наклейке — по этим цифрам ищут штуку в коробе. */
  sticker_part_a: string | null;
  sticker_part_b: string | null;
  box_position: number | null;
}

export interface SupplyShipping {
  shipping_type: string | null;
  shipping_point_id: number | null;
  shipping_date: string | null;
  waybill_uuid: string | null;
}

export interface SupplyClosing {
  supply_id: string;
  wb_supply_id: string;
  cabinet_id: number;
  cabinet_name: string;
  status: string;
  boxes_open: number;
  boxes_total: number;
  unpicked: UnpickedTask[];
  /** Отменённые покупателем задания этой поставки — их надо вынуть из коробов. */
  cancelled: UnpickedTask[];
  supply: Supply;
  /** Поставки-парковки: в доставку не уезжают, но должны быть видны. */
  carryover_supplies: string[];
  documents: { kind: string; number: string; cabinet_id: number | null }[];
  /** Параметры отгрузки: заполняют один раз, а заходят на экран много. */
  shipping: SupplyShipping;
  /** ПТМО не нужна: продажу целиком покрыл остаток юрлица в комнате. */
  ptmo_skipped: boolean;
  /** Закрытие требует проведённых документов 1С (поставка заведена после
   *  выкатки проверки). `false` — старая поставка: закрывается по-старому,
   *  состояние документов не проверяется и не запрашивается. */
  close_requires_posted_docs: boolean;
}

/** Состояние документов 1С поставки (истина — 1С, считает сервер).
 *  `nothing_to_document` — в поставке нет собранных товаров, документировать
 *  нечего; `missing` — каких-то документов цепочки нет; `unposted` — есть, но
 *  не проведены; `posted` — всё проведено; `stale` — собранного не осталось,
 *  а документы 1С живы (`stale`): их надо пометить на удаление в 1С. */
export type DocumentsCheckState =
  | "nothing_to_document"
  | "missing"
  | "unposted"
  | "posted"
  | "stale";

export interface DocumentsCheck {
  state: DocumentsCheckState;
  unposted: { kind: string; number: string }[];
  /** Виды документов цепочки, которых ещё нет (`kind`, как у `documents`). */
  missing: string[];
  /** `stale`: документы, которые остались в 1С при пустой поставке. */
  stale: { kind: string; number: string }[];
}

/** Способ сканирования: при 'tsd' кнопки камеры на экранах нет. */
export interface FbsConfig {
  scan_method: "tsd" | "camera" | "both";
  /** Инсталляция хранит токены WB у себя → в настройках кабинета есть поле
   *  ввода. Там, где токены в Infisical, вводить в портале нечего. */
  wb_token_in_portal: boolean;
  /** Ведутся ли партии в 1С. Где не ведутся, номер партии не спрашиваем. */
  partia: boolean;
  /** Ссылки завоза в комнату: ими предзаполняются фильтры списка перемещений,
   *  чтобы кладовщик не выставлял одно и то же руками (а промахнувшись — не
   *  искал свой заказ и не заводил второй). */
  transfer_org_ref: string;
  transfer_from_ref: string;
  transfer_to_ref: string;
  /** Типы товара для фильтра пунктов отгрузки. Подписи приходят с сервера —
   *  вторая их копия во фронте разъехалась бы молча. */
  cargo_types: { value: number; title: string }[];
}

export interface RelayAgentStatus {
  agent: string;
  connected: boolean;
}

export interface PrintStatus {
  /** Подключён ли агент: без `point_id` — общий (`default`), с ним — агент точки. */
  relay_connected: boolean;
  printer_target: string | null;
  configured: boolean;
  /** Реестр агентов. Ключа НЕТ, пока в инсталляции нет точек сборки. */
  agents?: RelayAgentStatus[];
  /** Заполнен только в ответе на `?point_id=`. */
  /** `printer_target` — принтер точки для ЗАПРОШЕННОГО назначения (`purpose`);
   *  `null` — у точки он не задан. */
  point?: { id: number; name: string; agent: string; printer_target: string | null };
}

export interface PrintResult {
  ok: boolean;
  error: string | null;
}

// --- запросы -----------------------------------------------------------------

export async function getOverview(): Promise<FbsOverview> {
  const { data } = await api.get<FbsOverview>("/fbs/overview");
  return data;
}

/** Подтянуть новые заказы WB. Можно дёргать когда угодно, в том числе в сборе. */
export async function syncOrders(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>("/fbs/sync");
  return data;
}

export async function getSupply(supplyId: string): Promise<Supply> {
  const { data } = await api.get<Supply>(`/fbs/supplies/${supplyId}`);
  return data;
}

/** Состояние экрана сбора целиком. Перечитывается после каждого скана. */
export async function getPickState(
  cabinetId: number,
  warehouseId?: number | null,
  b2b = false,
): Promise<PickState> {
  const { data } = await api.get<PickState>(
    `/fbs/cabinets/${cabinetId}/pick-state`,
    {
      params: { ...(warehouseId ? { wb_warehouse_id: warehouseId } : {}), b2b },
    },
  );
  return data;
}

/** Остаток комнаты — обращение в 1С. Тянуть редко: на горячем пути ему не место. */
export async function getRoomStock(
  cabinetId: number,
  wbWarehouseId?: number | null,
): Promise<RoomStock> {
  const { data } = await api.get<RoomStock>(
    `/fbs/cabinets/${cabinetId}/room-stock`,
    {
      // Комната своя у каждого склада продавца — без склада вернулся бы остаток
      // чужого помещения.
      params: wbWarehouseId ? { wb_warehouse_id: wbWarehouseId } : undefined,
    },
  );
  return data;
}

/** Наименования по артикулам — тоже обращение в 1С. Тянуть редко (как остаток
 *  комнаты): списки перечитываются после каждого скана, а наименования не
 *  меняются. Справочник общий на все юрлица, кабинет здесь — только адрес ручки. */
/** Артикул → наименование 1С. Справочник общий на все юрлица. */
export async function getArticleNames(): Promise<ArticleNames> {
  const { data } = await api.get<ArticleNames>("/fbs/article-names");
  return data;
}

/** Один скан. Поставку заводит сам бэкенд — первым сканом, а не входом на экран. */
export async function pick(
  cabinetId: number,
  payload: PickRequest,
): Promise<PickResult> {
  const { data } = await api.post<PickResult>(
    `/fbs/cabinets/${cabinetId}/pick`,
    payload,
  );
  return data;
}

/** Строка списка поставок кабинета — путь назад к уже уехавшей. */
export interface SupplyListRow {
  supply_id: string;
  wb_supply_id: string;
  status: string;
  is_b2b: boolean;
  warehouse_name: string;
  created_at: string;
  delivered_at: string | null;
  closed_at: string | null;
  picks: number;
  issues: number;
  documents: number;
}

export async function listSupplies(cabinetId: number): Promise<SupplyListRow[]> {
  const { data } = await api.get<SupplyListRow[]>(`/fbs/cabinets/${cabinetId}/supplies`);
  return data;
}

export async function getIssues(
  supplyId: string,
  cabinetId?: number,
): Promise<Issues> {
  const { data } = await api.get<Issues>(`/fbs/supplies/${supplyId}/issues`, {
    params: cabinetId ? { cabinet_id: cabinetId } : undefined,
  });
  return data;
}

/** Завести грузоместа на все коробá поставки и напечатать QR пачкой. */
export interface BoxLabels {
  /** Номера коробов, на которые завели грузоместа и напечатали QR. */
  printed: number[];
  /** Нарушения правил ПВЗ: печать не блокируют, но их надо увидеть. */
  warnings: string[];
}

/** Напечатать этикетки коробов.
 *
 * `reprint` — печатать ВСЕ непустые заново, а не только ненапечатанные:
 * этикетку рвут, заливают и теряют, а копия ничего в учёте не меняет (состав
 * грузоместа WB не хранит). */
export async function printBoxLabels(
  supplyId: string,
  reprint = false,
): Promise<BoxLabels> {
  const { data } = await api.post<BoxLabels>(
    `/fbs/supplies/${supplyId}/labels`,
    undefined,
    { params: reprint ? { reprint: true } : undefined },
  );
  return data;
}

export async function getClosing(supplyId: string): Promise<SupplyClosing> {
  const { data } = await api.get<SupplyClosing>(
    `/fbs/supplies/${supplyId}/closing`,
  );
  return data;
}

/** Проведённость документов 1С поставки — отдельно от экрана: читает 1С. */
export async function getDocumentsCheck(supplyId: string): Promise<DocumentsCheck> {
  const { data } = await api.get<DocumentsCheck>(
    `/fbs/supplies/${supplyId}/documents-check`,
  );
  return data;
}

export async function refreshStatuses(
  supplyId: string,
): Promise<{ cancelled: number[]; external: number[]; errors: string[] }> {
  const { data } = await api.post(`/fbs/supplies/${supplyId}/refresh-statuses`);
  return data;
}

export async function taskAction(
  taskId: string,
  action: "cancel" | "defer",
  supplyId: string,
): Promise<SupplyClosing> {
  const { data } = await api.post<SupplyClosing>(
    `/fbs/tasks/${taskId}/action`,
    {
      action,
      supply_id: supplyId,
    },
  );
  return data;
}

/** Отложить всё несобранное по юрлицу — одной кнопкой. */
export async function deferAll(supplyId: string): Promise<SupplyClosing> {
  const { data } = await api.post<SupplyClosing>(
    `/fbs/supplies/${supplyId}/defer-all`,
  );
  return data;
}

export async function reprintBoxQr(boxId: string): Promise<PrintResult> {
  const { data } = await api.post<PrintResult>(`/fbs/boxes/${boxId}/print-qr`);
  return data;
}

export async function closeBox(boxId: string, force = false): Promise<Box> {
  const { data } = await api.post<Box>(`/fbs/boxes/${boxId}/close`, { force });
  return data;
}

export async function getBoxItems(boxId: string): Promise<BoxItem[]> {
  const { data } = await api.get<BoxItem[]>(`/fbs/boxes/${boxId}/items`);
  return data;
}

export async function deletePick(pickId: string): Promise<void> {
  await api.delete(`/fbs/picks/${pickId}`);
}

export interface RetryAll extends Issues {
  tried: number;
  fixed: number;
  failed: number;
  first_error: string;
}

/** «Повторить всё» — один проход «Разбора» пачкой.
 *
 * ⚠️ Пачкой, а не целиком: каждая позиция — обращение к ЧЗ и в 1С, и двести
 * штук в одном запросе упёрлись бы в таймаут. Экран повторяет вызов, пока
 * `fixed > 0`. */
export async function retryAllIssues(
  supplyId: string,
  cabinetId?: number,
  offset = 0,
): Promise<RetryAll> {
  const { data } = await api.post<RetryAll>(
    `/fbs/supplies/${supplyId}/issues/retry-all`,
    null,
    {
      params: {
        ...(cabinetId ? { cabinet_id: cabinetId } : {}),
        ...(offset ? { offset } : {}),
      },
    },
  );
  return data;
}

export async function retryIssue(
  pickId: string,
  cabinetId?: number,
): Promise<Issues> {
  const { data } = await api.post<Issues>(`/fbs/picks/${pickId}/retry`, null, {
    params: cabinetId ? { cabinet_id: cabinetId } : undefined,
  });
  return data;
}

/** Перепечатать стикер задания: и когда он не напечатался, и когда испорчен. */
export async function reprintPickSticker(pickId: string): Promise<PrintResult> {
  const { data } = await api.post<PrintResult>(`/fbs/picks/${pickId}/reprint`);
  return data;
}

/** Позиция с отправленным КИЗ и статусом его проверки у WB. */
export interface SgtinRow {
  pick_id: string;
  wb_order_id: number;
  sticker_part_a: string | null;
  sticker_part_b: string | null;
  article: string;
  nomenclature_name: string;
  decision: string | null;
  decision_text: string | null;
  /** Не пустит поставку в доставку (в т.ч. «проверка ещё идёт»). */
  blocking: boolean;
  checked_at: string | null;
}

export interface SgtinCheck {
  rows: SgtinRow[];
  checked: number;
  blocking: number;
}

/** «Отправить без кода»: забракованный WB КИЗ — из задания (явное действие, только
 *  на ошибке КИЗ в открытой поставке). Ответ — строка позиции в схеме
 *  «Разбора»; отказ по состоянию — 409, отказ WB — 502 с его текстом. */
export async function removeSgtin(pickId: string): Promise<IssueRow> {
  const { data } = await api.post<IssueRow>(`/fbs/picks/${pickId}/sgtin-remove`);
  return data;
}

/** «Отправить КИЗ повторно»: WB код не закрепил или в задании чужой — ТОЛЬКО
 *  повторный `PUT` того же КИЗ (не «Повторить» «Разбора», который чинит причины
 *  по порядку). Ответ — строка позиции в схеме «Разбора»; не тот статус — 409,
 *  WB не принял — 502 с его текстом. */
export async function resendSgtin(pickId: string): Promise<IssueRow> {
  const { data } = await api.post<IssueRow>(`/fbs/picks/${pickId}/sgtin-resend`);
  return data;
}

/** «Заменить товар»: КИЗ другого экземпляра того же товара — в то же задание WB.
 *  Контракт — как у скана ЧЗ (`PickRequest`, ветка `cim`). */
export interface SgtinReplaceRequest {
  cim_code: string;
  gtin: string;
  serial: string;
}

/** Ответ замены: строка «Разбора» + что сказать человеку о WB (например,
 *  прежний срок годности остался в задании — удалить его WB не даёт). */
export interface SgtinReplaceOut extends IssueRow {
  warning: string | null;
}

export async function replaceSgtin(
  pickId: string,
  body: SgtinReplaceRequest,
): Promise<SgtinReplaceOut> {
  const { data } = await api.post<SgtinReplaceOut>(`/fbs/picks/${pickId}/sgtin-replace`, body);
  return data;
}

/** «Проверить маркировку»: только чтение WB, статусы пишутся к позициям. */
export async function checkSgtin(supplyId: string): Promise<SgtinCheck> {
  const { data } = await api.post<SgtinCheck>(`/fbs/supplies/${supplyId}/sgtin-check`);
  return data;
}

/** Забракованная WB штука — строка красного сообщения на экране сбора. */
export interface SgtinRejected {
  pick_id: string;
  /** Чья штука: опрос идёт по всем открытым поставкам склада (разные юрлица). */
  supply_id: string;
  wb_supply_id: string;
  cabinet_name: string;
  wb_order_id: number;
  /** Номер короба, как его видят люди («Короб 3»). */
  box_position: number | null;
  sticker_part_a: string | null;
  sticker_part_b: string | null;
  article: string;
  nomenclature_name: string;
  decision: string;
  decision_text: string;
  /** WB требует КИЗ обязательно — «Отправить без кода» откажет, только замена. */
  sgtin_required: boolean;
  /** WB код не закрепил / в задании чужой — «Отправить КИЗ повторно» (`resendSgtin`). */
  resend: boolean;
}

/** Поставка, по которой опрос WB не удался (остальные опрошены). */
export interface SgtinPollError {
  supply_id: string;
  wb_supply_id: string;
  cabinet_name: string;
  detail: string;
}

export interface SgtinPoll {
  rejected: SgtinRejected[];
  /** Отправленные КИЗ без итога проверки (не читали, «проверка идёт»,
   *  «WB ещё не закрепил»). */
  unresolved: number;
  /** Спрашивали ли WB этим запросом (иначе — состояние из БД). */
  polled: boolean;
  errors: SgtinPollError[];
}

/** Фоновая проверка КИЗ с экрана сбора по ВСЕМ открытым поставкам складов
 *  человека (не только поставке экрана): WB спрашивается только по неитоговым
 *  и не чаще раза в 15 с на поставку (троттлит сервер). */
export async function pollSgtin(): Promise<SgtinPoll> {
  const { data } = await api.post<SgtinPoll>(`/fbs/sgtin-poll`);
  return data;
}

export async function deliverSupply(
  supplyId: string,
): Promise<{ delivered: string }> {
  const { data } = await api.post(`/fbs/supplies/${supplyId}/deliver`);
  return data;
}

export async function createSaleDocuments(
  supplyId: string,
): Promise<{ documents: { cabinet: string; kind: string; number: string }[] }> {
  const { data } = await api.post(`/fbs/supplies/${supplyId}/sale-documents`);
  return data;
}

/** Закрыть поставку — юрлицо освобождается под следующую. */
export async function closeSupply(supplyId: string): Promise<FbsOverview> {
  const { data } = await api.post<FbsOverview>(
    `/fbs/supplies/${supplyId}/close`,
  );
  return data;
}

export async function getConfig(): Promise<FbsConfig> {
  const { data } = await api.get<FbsConfig>("/fbs/config");
  return data;
}

/** Состояние канала печати. `pointId` переводит ответ на ЕЁ агента и ЕЁ
 *  принтер: печатать точка будет не на глобальный, и спрашивать про него на
 *  её экране бессмысленно. */
export async function getPrintStatus(pointId?: number | null): Promise<PrintStatus> {
  const { data } = await api.get<PrintStatus>("/fbs/print/status", {
    params: pointId ? { point_id: pointId } : undefined,
  });
  return data;
}

export async function printTestLabel(
  purpose = "fbs",
  pointId?: number | null,
): Promise<PrintResult> {
  const { data } = await api.post<PrintResult>("/fbs/print/test", null, {
    params: { purpose, ...(pointId ? { point_id: pointId } : {}) },
  });
  return data;
}

// --- точки сборки (админ) ----------------------------------------------------

/** Комната точки — склад 1С (комната ФБС). В неё «смотрят» склады ВБ и Ozon
 *  кабинетов (`one_c_warehouse_ref` склада); комната — ровно в одной точке. */
export interface PointRoom {
  one_c_warehouse_ref: string;
  name: string;
}

/**
 * Точка сборки: помещение со своим relay-агентом и своим принтером.
 *
 * Токена здесь нет и быть не может — в базе только его хэш, открытое значение
 * живёт ровно один ответ выпуска. Наружу отдаётся лишь факт «выпускался».
 */
export interface FbsPoint {
  id: number;
  name: string;
  position: number;
  relay_agent: string;
  /** Принтеры точки по назначениям (`fbs` — всегда, `ozon` — если задан). */
  printers: PointPrinters;
  relay_token_issued: boolean;
  /** Склад-источник завоза ЭТОЙ точки. Пусто — завоз в её склады едет с
   *  системной настройки `fbs_source_wh_ref` (умолчание названо в форме). */
  source_wh_ref: string | null;
  source_wh_name: string | null;
  /** Комнаты точки в порядке `position`. Склад МП попадает в точку через
   *  свою комнату. */
  rooms: PointRoom[];
  user_ids: number[];
}

/** Принтеры точки: назначение → адрес. `fbs` обязателен, `ozon` — нет;
 *  другие назначения сервер отбивает 422. */
export type PointPrinters = { fbs: string; ozon?: string };

/** Заведение точки: имя, агент и принтер обязательны сразу — наследования от
 *  глобального принтера нет (иначе печатали бы в чужое помещение). */
export interface PointCreatePayload {
  name: string;
  relay_agent: string;
  printers: PointPrinters;
  position?: number;
  /** Люди точки — ПОЛНЫЙ состав. Привязка задаётся только в точке: из карточки
   *  пользователя она убрана, чтобы в одну таблицу не писали две формы. */
  user_ids: number[];
  /** Склад-источник завоза — необязательное переопределение системной
   *  настройки. Ссылка и имя ходят ПАРОЙ: без имени сервер отвечает 422. */
  source_wh_ref?: string | null;
  source_wh_name?: string | null;
  /** Комнаты (ссылки складов 1С) сразу — необязательно. */
  room_refs?: string[];
}

/** Правка точки. `room_refs` и `user_ids` — ПОЛНАЯ замена состава
 *  комнат и людей: «убрать комнату» и «убрать человека» иначе не выразить.
 *
 *  ⚠️ У склада-источника `null` — ЗНАЧАЩЕЕ значение: «сбросить на системную
 *  настройку». Поле, которого нет в теле, сервер не трогает, поэтому пару
 *  шлём только когда её меняли. */
export interface PointUpdatePayload {
  name?: string;
  relay_agent?: string;
  /** ПОЛНАЯ замена принтеров точки. */
  printers?: PointPrinters;
  position?: number;
  room_refs?: string[];
  user_ids?: number[];
  source_wh_ref?: string | null;
  source_wh_name?: string | null;
}

/** Свежевыпущенный токен агента точки: показывается ОДИН раз. */
export interface PointRelayToken {
  agent: string;
  token: string;
}

/** Склад ВБ-кабинета в списке «вне точек». */
export interface PointUnassignedWarehouse {
  cabinet_id: number;
  cabinet_name: string;
  wb_warehouse_id: number;
  name: string;
}

/** Склад Ozon-кабинета в списке «вне точек» (у Ozon своя нумерация id). */
export interface PointUnassignedOzonWarehouse {
  cabinet_id: number;
  cabinet_name: string;
  ozon_warehouse_id: number;
  name: string;
}

/** Комната, известная кабинетам, не отданная ни в одну точку, — с её складами. */
export interface PointUnassignedRoom {
  one_c_warehouse_ref: string;
  name: string;
  warehouses: PointUnassignedWarehouse[];
  ozon_warehouses: PointUnassignedOzonWarehouse[];
}

/** Чего сборщики не увидят: ничьи комнаты и склады без комнаты (последние в
 *  точку не отдать, пока в кабинете складу не задан склад 1С). Нужен админке —
 *  пропажу склада иначе замечают по «заданий нет». */
export interface PointUnassigned {
  rooms: PointUnassignedRoom[];
  warehouses_without_room: PointUnassignedWarehouse[];
  ozon_warehouses_without_room: PointUnassignedOzonWarehouse[];
}

export async function listPoints(): Promise<FbsPoint[]> {
  const { data } = await api.get<FbsPoint[]>("/fbs/points");
  return data;
}

/** Комнаты вне точек и склады без комнаты. Списки пусты, когда точек нет
 *  вовсе — понятие неприменимо. */
export async function listUnassigned(): Promise<PointUnassigned> {
  const { data } = await api.get<PointUnassigned>("/fbs/points/unassigned");
  return data;
}

export async function createPoint(payload: PointCreatePayload): Promise<FbsPoint> {
  const { data } = await api.post<FbsPoint>("/fbs/points", payload);
  return data;
}

export async function updatePoint(
  id: number,
  payload: PointUpdatePayload,
): Promise<FbsPoint> {
  const { data } = await api.put<FbsPoint>(`/fbs/points/${id}`, payload);
  return data;
}

export async function deletePoint(id: number): Promise<void> {
  await api.delete(`/fbs/points/${id}`);
}

/** Выпустить токен relay-агента точки. Ответ — единственный показ токена;
 *  повторный выпуск ЗАМЕНЯЕТ старый и отцепляет настроенный по нему ПК. */
export async function issuePointRelayToken(id: number): Promise<PointRelayToken> {
  const { data } = await api.post<PointRelayToken>(`/fbs/points/${id}/relay-token`);
  return data;
}

// --- кабинеты (админ) --------------------------------------------------------

export async function listCabinets(): Promise<Cabinet[]> {
  const { data } = await api.get<Cabinet[]>("/fbs/cabinets");
  return data;
}

export async function createCabinet(payload: CabinetPayload): Promise<Cabinet> {
  const { data } = await api.post<Cabinet>("/fbs/cabinets", payload);
  return data;
}

export async function updateCabinet(
  id: number,
  payload: Partial<CabinetPayload>,
): Promise<Cabinet> {
  const { data } = await api.patch<Cabinet>(`/fbs/cabinets/${id}`, payload);
  return data;
}

export async function deleteCabinet(id: number): Promise<void> {
  await api.delete(`/fbs/cabinets/${id}`);
}

export async function testCabinet(
  id: number,
): Promise<{
  ok: boolean;
  error?: string;
  orders_total?: number;
  orders_on_warehouse?: number;
}> {
  const { data } = await api.post(`/fbs/cabinets/${id}/test`);
  return data;
}

// --- заказы на перемещение (завоз в комнату ФБС) -----------------------------

/** Строка предпросмотра: сколько штук ещё НЕ собрано.
 *
 *  Остаток комнаты здесь не участвует: кладовщик сначала собирает всё, что в
 *  комнате есть, и только потом заказывает перемещение — значит несобранное
 *  осталось именно потому, что товара в комнате нет.
 */
export interface TransferOrderNeedRow {
  nomenclature_ref: string;
  nomenclature_name: string;
  characteristic_ref: string;
  characteristic_name: string;
  article: string;
  needed: number;
}

/** Потребность одной КОМНАТЫ. У юрлица с двумя складами продавца таких строк
 *  две — по документу на каждую, потому что склад-получатель у них разный. */
export interface TransferOrderCabinet {
  /** `wb` | `ozon` — часть ключа кабинета. */
  marketplace: string;
  cabinet_id: number;
  cabinet_name: string;
  wb_warehouse_id: number | null;
  warehouse_name: string;
  rows: TransferOrderNeedRow[];
  total: number;
}

export interface TransferOrderPreview {
  cabinets: TransferOrderCabinet[];
  /** Задания без опознанной номенклатуры 1С — заказ по ним будет неполным. */
  unresolved_articles: string[];
  source_wh_ref: string;
  source_wh_name: string;
}

export interface TransferOrderCreated {
  marketplace: string;
  cabinet_id: number;
  cabinet_name: string;
  number: string;
  lines: number;
}

export async function getTransferOrderPreview(): Promise<TransferOrderPreview> {
  const { data } = await api.get<TransferOrderPreview>(
    "/fbs/transfer-orders/preview",
  );
  return data;
}

/**
 * ⚠️ Выбор уходит ПАРАМИ (кабинет, маркетплейс). Комната у ВБ и Ozon одна,
 * кабинеты живут в разных таблицах, а id у обоих целые — голая цифра означала
 * бы сразу два разных кабинета, и завоз Ozon создался бы по заданиям ВБ.
 */
export async function createTransferOrders(
  cabinets: { cabinet_id: number; marketplace: string }[],
): Promise<{ created: TransferOrderCreated[] }> {
  const { data } = await api.post("/fbs/transfer-orders", { cabinets });
  return data;
}

// --- параметры отгрузки поставки (новое требование WB, сентябрь 2026) --------

export interface ShippingPoint {
  id: number;
  name: string;
  address: string;
  city: string;
}

/** Способы доставки до пункта отгрузки — ровно те, что принимает WB. */
export const SHIPPING_SELF = "selfShipping";
export const SHIPPING_COMPANY = "transportCompany";

/**
 * Пункты отгрузки кабинета. Выбор, а не ввод: id придумать нельзя.
 *
 * ⚠️ Населённый пункт и тип товара WB требует обязательно — без них он
 * отвечает 400, то есть список пуст и пункт невыбираем. Умолчаний нет: город
 * называет тот, кто повезёт, а тип товара определяет, какие пункты его примут.
 */
export async function getShippingPoints(
  supplyId: string,
  params: { city: string; cargo_type: number },
): Promise<ShippingPoint[]> {
  const { data } = await api.get<ShippingPoint[]>(
    `/fbs/supplies/${supplyId}/shipping-points`,
    { params },
  );
  return data;
}

export async function setSupplyShipping(
  supplyId: string,
  body: {
    shipping_type: string;
    shipping_point_id: number;
    shipping_date: string;
    waybill_uuid?: string | null;
  },
): Promise<SupplyShipping> {
  const { data } = await api.put<SupplyShipping>(`/fbs/supplies/${supplyId}/shipping`, body);
  return data;
}
